import map from "lodash/map";
import orderBy from "lodash/orderBy";
import { useCustomValueTags } from "PFApp/hooks/use_custom_value_tags";
import { useExperienceConfig } from "PFApp/hooks/use_experience_config";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import { getProfileFrameworkSkills } from "PFApp/profiles/show/profile_show_page.utils";
import { Inline, Stack } from "PFComponents/containers";
import { CustomValuePill } from "PFComponents/custom_value_pill";
import { Divider } from "PFComponents/divider";
import { Typography } from "PFComponents/typography";
import { useSkillsType } from "PFCore/hooks/use_skills_type";
import { CurrentProfile, CustomValue, Experience, Profile, SkillsFramework } from "PFTypes";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { EditableCustomValuePill } from "./editable_custom_value_pill";
import css from "./skills_frameworks_suggested_skills_modal.module.scss";

type SkillsFrameworkRowProps = {
  skillsFramework: SkillsFramework;
  profile: Profile | CurrentProfile;
  canEditSkills: boolean;
  onSkillRanked?: (customValue: Partial<CustomValue>) => void;
};

export const SkillsFrameworkRow = ({
  skillsFramework,
  profile,
  canEditSkills,
  onSkillRanked
}: SkillsFrameworkRowProps) => {
  const { t } = useTranslation("profiles", { keyPrefix: "show.parts.skillsModal.suggestions.framework" });
  const { t: tCore } = useTranslation("core");
  const skillsType = useSkillsType();
  const { experienceLevels } = useExperienceConfig();
  const experienceLabelMap = useExperienceLabelMap();
  const { getCustomValueTags } = useCustomValueTags();

  const skills = useMemo(
    () =>
      orderBy(getProfileFrameworkSkills(skillsFramework, profile), [
        ({ experience }) => !experience,
        "value"
      ]),
    [profile, skillsFramework]
  );

  const getMatchedCustomFields = useCallback(
    (skillsFramework: SkillsFramework) => {
      const customFieldsValues = skillsFramework.skillsFrameworkCustomTypes.map(
        ({ displayAs, skillsFrameworkCustomValues }) => {
          let formattedValues = map(skillsFrameworkCustomValues, "value").join(", ");
          if (skillsFrameworkCustomValues.length > 1) {
            formattedValues = `(${formattedValues})`;
          }

          return `${displayAs} = ${formattedValues}`;
        }
      );

      return customFieldsValues.join(` ${tCore("and")} `);
    },
    [tCore]
  );

  return (
    <Stack>
      <Typography variant="h5" className={css.sectionTitle}>
        {t("skillsFrameworkName", { name: skillsFramework.name })}
      </Typography>
      <Typography variant="bodyRegular">
        {t("description", { matchedCustomFields: getMatchedCustomFields(skillsFramework) })}
      </Typography>
      <Inline wrap="wrap" alignItems="center">
        {skills.map((skill, index, array) => (
          <Inline key={skill.id} alignItems="center">
            {canEditSkills ? (
              <EditableCustomValuePill
                key={skill.id}
                id={skill.id}
                name={String(skill.value)}
                experience={skill.experience}
                requiredExperience={skill.requiredExperience as Experience}
                customTypeName={skillsType!.name}
                customTypeAriaLabel={skillsType!.display_as}
                tags={getCustomValueTags(skill.tagIds)}
                onChange={onSkillRanked}
              />
            ) : (
              <CustomValuePill
                name={String(skill.value)}
                experienceLevels={experienceLevels}
                experienceLabelMap={experienceLabelMap}
                experience={skill.experience}
                requiredExperience={skill.requiredExperience}
                tags={getCustomValueTags(skill.tagIds)}
              />
            )}
            {index < array.length - 1 && <Divider type="vertical" className={css.skillDivider} />}
          </Inline>
        ))}
      </Inline>
    </Stack>
  );
};
